import React, { useState, useEffect } from "react";
import Button from "../../component/shared/Button";
import WatchDemoVideoButton from "../../component/shared/WatchDemoVideoButton";
import AddNewEssayModal from "./AddNewEssayModal";
import { Api } from "../../network/api";

const ModalAnswer = () => {
  const [essays, setEssays] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedEssay, setSelectedEssay] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const fetchModalAnswers = async () => {
    try {
      setIsLoading(true);
      const response = await Api.getModalAnswer();
      const fetchedEssays = response?.data || [];
      const sortedEssays = fetchedEssays?.sort((a, b) => {
        const dateA = new Date(a.created_at || a.uploaded_at || 0);
        const dateB = new Date(b.created_at || b.uploaded_at || 0);
        return dateB - dateA;
      });
      setEssays(sortedEssays);
    } catch (error) {
      console.error("Error fetching modal answers:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchModalAnswers();
  }, []);

  const handleAddEssay = (essayData) => {
    setShowSuccessMessage(true);

    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);
    setTimeout(() => {
      fetchModalAnswers();
    }, 500);
  };

  const handleViewDetails = (essay) => {
    setSelectedEssay(essay);
    setIsDetailsModalOpen(true);
  };

  const closeDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedEssay(null);
  };

  const handleFileView = (essay) => {
    const fileUrl = essay.file_url || essay.model_answer || essay.file;

    if (!fileUrl) {
      console.warn("No file URL found.");
      return;
    }
    if (fileUrl.startsWith("http://") || fileUrl.startsWith("https://")) {
      window.open(fileUrl, "_blank");
      return;
    }

    const backendBaseUrl = "https://backend.calbarbible.com/";
    const finalUrl = `${backendBaseUrl}${fileUrl.replace(/^\/+/, "")}`;
    window.open(finalUrl, "_blank");
  };


  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {showSuccessMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg
              className="h-5 w-5 text-green-400"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <p className="ml-3 text-sm font-medium text-green-800">
              Model answer uploaded successfully!
            </p>
            <button
              onClick={() => setShowSuccessMessage(false)}
              className="ml-auto text-green-400 hover:text-green-600"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row md:items-center space-y-4 lg:space-y-0 justify-between">
            <div>
              <h1 className="md:text-2xl text-xl font-extrabold text-gray-900">
                Model Answers
              </h1>
              <p className="text-gray-600 mt-1">
                Manage and view model answers for different subjects.
              </p>
            </div>

            <div className="flex items-center space-x-3">
              <WatchDemoVideoButton
                videoUrl={"https://calbarbible-static.s3.us-east-1.amazonaws.com/static/Modal+Answer+.mov"}
                title="Modal Answer Demo Video"
              />
              <Button
                onClick={() => setIsAddModalOpen(true)}
                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800"
              >
                Add Model Answer
              </Button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
              <span className="ml-3 text-gray-600">
                Loading model answers...
              </span>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subject Name
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Year
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {essays.map((essay) => (
                  <tr
                    key={essay.id}
                    className="hover:bg-gray-50 transition-colors duration-150"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {essay?.subject_name || essay?.subject}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{essay?.year}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <Button
                        size="small"
                        variant="secondary"
                        onClick={() => handleViewDetails(essay)}
                      >
                        View Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {!isLoading && essays?.length === 0 && (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-12 h-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No model answers found
            </h3>
            <p className="text-gray-600 mb-6">
              Get started by adding your first model answer.
            </p>
            <Button
              onClick={() => setIsAddModalOpen(true)}
              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800"
            >
              Add Your First Model Answer
            </Button>
          </div>
        )}
      </div>

      <AddNewEssayModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAddEssay}
        existingEssays={essays}
      />
      {isDetailsModalOpen && selectedEssay && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={closeDetailsModal}
            />
            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-red-800 px-6 py-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-white">
                    Model Answer Details
                  </h3>
                  <button
                    onClick={closeDetailsModal}
                    className="text-white hover:text-gray-200 transition-colors duration-200"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Subject
                    </h4>
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-gray-900 font-medium text-sm md:text-lg">
                        {selectedEssay.subject_name || selectedEssay.subject}
                      </p>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Year
                    </h4>
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-gray-900 font-medium text-sm md:text-lg">
                        {selectedEssay.year}
                      </p>
                    </div>
                  </div>
                </div>

                {(selectedEssay.file_url ||
                  selectedEssay.model_answer ||
                  selectedEssay.file) && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        File
                      </h4>
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="text-gray-900 text-sm font-medium mb-1">
                              {selectedEssay.file_name ||
                                selectedEssay.original_name ||
                                "Model Answer File"}
                            </p>
                            <p className="text-gray-600 text-xs">
                              {selectedEssay.file_size
                                ? `${(selectedEssay.file_size / 1024).toFixed(
                                  1
                                )} KB`
                                : ""}
                            </p>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleFileView(selectedEssay)}
                              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                              <svg
                                className="w-4 h-4 mr-2"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                              </svg>
                              View
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                {selectedEssay.extractedText && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Extracted Text
                    </h4>
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 max-h-40 overflow-y-auto">
                      <p className="text-gray-900 text-sm leading-relaxed">
                        {selectedEssay.extractedText}
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-end pt-6 border-t border-gray-200">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={closeDetailsModal}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModalAnswer;
